
import React, { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { QrCode, Check } from "lucide-react";
import { toast } from "sonner";
import { CertificateMetadata } from '@/types/certification';
import { format } from 'date-fns';

interface CertificateVerificationProps {
  certificates: CertificateMetadata[];
}

const CertificateVerification = ({ certificates }: CertificateVerificationProps) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationResult, setVerificationResult] = useState<CertificateMetadata | null>(null);

  const handleVerification = () => {
    if (!verificationCode.trim()) {
      toast.error('Please enter a verification code');
      return;
    }

    // Find certificate with matching verification code
    const certificate = certificates.find(
      cert => cert.verificationCode === verificationCode
    );

    if (certificate) {
      setVerificationResult(certificate);
      toast.success('Certificate verified successfully!');
    } else {
      setVerificationResult(null);
      toast.error('Invalid verification code');
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Certificate Verification</CardTitle>
          <CardDescription>
            Verify the authenticity of a certificate by entering its verification code
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4">
            <Input
              placeholder="Enter verification code"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              className="flex-1"
            />
            <Button onClick={handleVerification}>
              Verify Certificate
            </Button>
          </div>
        </CardContent>
      </Card>

      {verificationResult && (
        <Card className="border-green-200 bg-green-50 dark:bg-green-900/10 dark:border-green-900">
          <CardHeader>
            <div className="flex items-center">
              <Check className="h-5 w-5 text-green-500 mr-2" />
              <CardTitle className="text-lg text-green-700 dark:text-green-300">
                Certificate Verified
              </CardTitle>
            </div>
            <CardDescription>
              This certificate has been verified as authentic
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="bg-white dark:bg-background p-4 rounded-lg">
                <h3 className="font-medium text-lg">{verificationResult.programTitle}</h3>
                <p className="text-muted-foreground">{verificationResult.programCategory}</p>
                
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
                  <div>
                    <div className="text-sm text-muted-foreground">Certificate Number</div>
                    <div className="font-medium font-mono">{verificationResult.certificateNumber}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Issue Date</div>
                    <div className="font-medium">{format(new Date(verificationResult.issueDate), 'PP')}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Certification Level</div>
                    <div className="font-medium capitalize">{verificationResult.certificationLevel}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Status</div>
                    <div className="font-medium capitalize">{verificationResult.status}</div>
                  </div>
                  {verificationResult.expiryDate && (
                    <div>
                      <div className="text-sm text-muted-foreground">Expiry Date</div>
                      <div className="font-medium">{format(new Date(verificationResult.expiryDate), 'PP')}</div>
                    </div>
                  )}
                  <div>
                    <div className="text-sm text-muted-foreground">Issued By</div>
                    <div className="font-medium">{verificationResult.issuedBy}</div>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-center">
                <div className="bg-white dark:bg-background p-4 rounded-lg inline-block">
                  <QrCode className="h-24 w-24 mx-auto" />
                  <div className="text-center mt-2 text-sm text-muted-foreground">
                    Scan to verify
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CertificateVerification;
